import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import FolderCard from '../components/FolderCard';
import { Search, Upload, Image, FileText, Film, Trash2, Edit, X, FolderOpen, Folder, Lock, Users, Home, ArrowLeft, ArrowUpDown, ChevronRight } from 'lucide-react';
import BulkMediaUploader from '../components/BulkMediaUploader';
import AdminUploadTargetSelector from '../components/AdminUploadTargetSelector';
import FolderTree from '../components/FolderTree';
import axios from 'axios';
import { toast } from 'sonner';
import TagInput from '../components/TagInput';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate optimized image URL with fallback mechanism
 * Priority: Shopify CDN > DO Spaces > Local URL
 * 
 * @param {string|null} shopifyUrl - Shopify CDN URL
 * @param {number|null} width - Desired width (optional)
 * @param {number|null} height - Desired height (optional)
 * @param {string|null} doUrl - DO Spaces master URL (fallback)
 * @returns {string|null} - Optimized image URL or null
 */
const getShopifyImageUrl = (shopifyUrl, width, height, doUrl) => {
  // CRITICAL: Use the Shopify URL if it exists, otherwise fall back to the DO master URL.
  const effectiveBaseUrl = shopifyUrl || doUrl;

  if (!effectiveBaseUrl) return null; // Safety break

  // Use Shopify CDN parameters for resizing if Shopify URL is present
  if (shopifyUrl) {
    if (width || height) {
      const params = [];
      if (width) params.push(`width=${width}`);
      if (height) params.push(`height=${height}`);
      return shopifyUrl.includes('?') 
        ? `${shopifyUrl}&${params.join('&')}` 
        : `${shopifyUrl}?${params.join('&')}`;
    }
    return shopifyUrl;
  }

  // FALLBACK: For pending products or failed Shopify uploads, return the DO URL (serves original file).
  // Note: This incurs DO egress cost, but only for internal MCS views.
  return effectiveBaseUrl;
};

/**
 * Get all possible URLs for an image item for fallback purposes
 * @param {object} item - Media item
 * @returns {string[]} - Array of URLs to try in order
 */
const getImageFallbackUrls = (item) => {
  const urls = [];
  
  // Priority 1: DO Spaces URL (most reliable for production)
  if (item.do_source_url) {
    urls.push(item.do_source_url);
  }
  
  // Priority 2: Shopify CDN URL (optimized but may be temporary staged URL)
  if (item.shopify_cdn_url && !urls.includes(item.shopify_cdn_url)) {
    urls.push(item.shopify_cdn_url);
  }
  
  // Priority 3: File URL (local or already resolved)
  if (item.file_url) {
    const fileUrl = item.file_url.startsWith('http') 
      ? item.file_url 
      : `${API_URL}${item.file_url}`;
    if (!urls.includes(fileUrl)) {
      urls.push(fileUrl);
    }
  }
  
  return urls;
};

/**
 * Get the primary image URL to display
 * Uses DO Spaces as primary for reliability, with Shopify CDN as enhancement
 */
const getOptimizedImageUrl = (item, width = null, height = null) => {
  // Use DO Spaces as primary (most reliable for production deployment)
  if (item.do_source_url) {
    return item.do_source_url;
  }
  
  // Fallback to Shopify CDN if DO not available
  if (item.shopify_cdn_url) {
    return getShopifyImageUrl(item.shopify_cdn_url, width, height, null);
  }
  
  // Final fallback: Local file URL (legacy support)
  if (item.file_url) {
    if (item.file_url.startsWith('http')) {
      return item.file_url;
    }
    return `${API_URL}${item.file_url}`;
  }
  
  return null; // Safety break
};

const MediaGallery = () => {
  const { user, apiCall } = useAuth();
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folderHierarchy, setFolderHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState(null); // null = show all
  const [currentPath, setCurrentPath] = useState([]); // Breadcrumb navigation
  const [sortBy, setSortBy] = useState('date_desc'); // date_desc, date_asc, name_asc, name_desc, size_desc
  const [selectedFileType, setSelectedFileType] = useState('all');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [currentMedia, setCurrentMedia] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadFolder, setUploadFolder] = useState('');
  const [uploadTarget, setUploadTarget] = useState({ folder: 'admin', target_vendor_id: null });
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    alt_text: '',
    caption: '',
    tags: [],
    folder: ''
  });

  useEffect(() => {
    fetchMedia();
    fetchFolders();
    if (user?.role === 'admin') {
      fetchHierarchy();
    }
  }, [selectedFolder, selectedFileType]);

  const fetchHierarchy = async () => {
    try {
      const data = await apiCall('/api/media/folders/hierarchy');
      setFolderHierarchy(data);
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
    }
  };

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedFileType !== 'all') params.append('file_type', selectedFileType);
      if (selectedFolder) params.append('folder_path', selectedFolder);
      
      const queryString = params.toString();
      const url = `/api/media/${queryString ? '?' + queryString : ''}`;
      
      const data = await apiCall(url);
      setMedia(data);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const data = await apiCall('/api/media/folders/list');
      setFolders(data);
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const handleFolderNavigate = (folder) => {
    setSelectedFolder(folder.path);
    setCurrentPath([...currentPath, { name: folder.name, path: folder.path }]);
    fetchMedia();
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      // Go to root
      setSelectedFolder(null);
      setCurrentPath([]);
    } else {
      // Go to specific level
      const targetPath = currentPath[index];
      setSelectedFolder(targetPath.path);
      setCurrentPath(currentPath.slice(0, index + 1));
    }
    fetchMedia();
  };

  const sortMedia = (mediaList) => {
    const sorted = [...mediaList];
    switch (sortBy) {
      case 'name_asc':
        return sorted.sort((a, b) => a.original_filename.localeCompare(b.original_filename));
      case 'name_desc':
        return sorted.sort((a, b) => b.original_filename.localeCompare(a.original_filename));
      case 'size_desc':
        return sorted.sort((a, b) => b.file_size - a.file_size);
      case 'size_asc':
        return sorted.sort((a, b) => a.file_size - b.file_size);
      case 'date_asc':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'date_desc':
      default:
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    if (files.length > 0) {
      setIsUploadModalOpen(true);
    }
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    try {
      setUploading(true);
      const token = sessionStorage.getItem('token');
      
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadFolder) formData.append('folder', uploadFolder);

        // Skip server optimization for faster gallery uploads
        return fetch(`${API_URL}/api/media/upload?optimize=false`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      });

      await Promise.all(uploadPromises);
      toast.success(`${selectedFiles.length} file(s) uploaded successfully`);
      setIsUploadModalOpen(false);
      setSelectedFiles([]);
      setUploadFolder('');
      fetchMedia();
      fetchFolders();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateMedia = async () => {
    try {
      await axios.put(`${API_URL}/api/media/${currentMedia.id}`, formData);
      toast.success('Media updated successfully');
      setIsEditModalOpen(false);
      fetchMedia();
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Failed to update media');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/media/${mediaId}`);
      toast.success('Media deleted successfully');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  const openEditModal = (item) => {
    setCurrentMedia(item);
    setFormData({
      alt_text: item.alt_text || '',
      caption: item.caption || '',
      tags: item.tags || [],
      folder: item.folder || ''
    });
    setIsEditModalOpen(true);
  };

  const openDetailModal = (item) => {
    setCurrentMedia(item);
    setIsDetailModalOpen(true);
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="h-5 w-5" />;
      case 'video': return <Film className="h-5 w-5" />;
      case 'document': return <FileText className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredMedia = media.filter(item =>
    item.original_filename.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Media Library</h1>
            <p className="text-gray-500 mt-1">Upload and manage your files across folders</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => fileInputRef.current?.click()} variant="outline">
              <Upload className="h-4 w-4 mr-2" />
              Single Upload
            </Button>
            <Button onClick={() => setIsBulkUploadOpen(true)}>
              <Upload className="h-4 w-4 mr-2" />
              Bulk Upload
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*,.pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Bulk Upload Dialog */}
        <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Bulk Upload Files</DialogTitle>
            </DialogHeader>
            <BulkMediaUploader
              onUploadComplete={() => {
                setIsBulkUploadOpen(false);
                fetchMedia();
                fetchFolders();
              }}
              onClose={() => setIsBulkUploadOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Filters */}
        <Card className="max-w-full overflow-hidden">
          <CardContent className="pt-4 md:pt-6">
            {/* Breadcrumb Navigation */}
            {currentPath.length > 0 && (
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 overflow-x-auto pb-2">
                <button
                  onClick={() => handleBreadcrumbClick(-1)}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  <Home className="h-4 w-4" />
                  <span>Root</span>
                </button>
                {currentPath.map((crumb, index) => (
                  <React.Fragment key={crumb.id || crumb.name || `${crumb.title || "crumb"}-${index}`}>
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <button
                      onClick={() => handleBreadcrumbClick(index)}
                      className={`hover:text-gray-900 transition-colors truncate ${
                        index === currentPath.length - 1 ? 'font-semibold text-gray-900' : ''
                      }`}
                    >
                      {crumb.name}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            )}

            {/* Search and Sort Controls */}
            <div className="flex flex-col md:flex-row gap-3 md:gap-4">
              {/* Back Button (when in folder) */}
              {currentPath.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBreadcrumbClick(currentPath.length - 2)}
                  className="w-full md:w-auto"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}

              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* File Type Filter */}
              <Select value={selectedFileType} onValueChange={setSelectedFileType}>
                <SelectTrigger className="w-full md:w-[140px]">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="image">Images</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                  <SelectItem value="document">Documents</SelectItem>
                </SelectContent>
              </Select>

              {/* Sort By */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-[160px]">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date_desc">Newest First</SelectItem>
                  <SelectItem value="date_asc">Oldest First</SelectItem>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                  <SelectItem value="name_desc">Name Z-A</SelectItem>
                  <SelectItem value="size_desc">Largest First</SelectItem>
                  <SelectItem value="size_asc">Smallest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Media Grid with Folders */}
        {loading ? (
          <div className="text-center py-12">Loading media...</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
            {/* Show folders first (only at root or when not in a specific folder) */}
            {!selectedFolder && folders.map((folder) => (
              <FolderCard
                key={folder.path}
                folder={folder}
                onNavigate={handleFolderNavigate}
              />
            ))}

            {/* Show media files (sorted) */}
            {sortMedia(filteredMedia).map((item) => {
              // Get all possible URLs for fallback
              const fallbackUrls = getImageFallbackUrls(item);
              const primaryUrl = fallbackUrls[0] || null;
              
              return (
              <Card 
                key={item.id} 
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => openDetailModal(item)}
              >
                <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                  {item.file_type === 'image' && primaryUrl ? (
                    <img 
                      src={primaryUrl} 
                      alt={item.alt_text || item.original_filename || 'Media'} 
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        // Try fallback URLs on error
                        const currentSrc = e.target.src;
                        const currentIndex = fallbackUrls.indexOf(currentSrc);
                        const nextIndex = currentIndex + 1;
                        
                        if (nextIndex < fallbackUrls.length) {
                          e.target.src = fallbackUrls[nextIndex];
                        } else {
                          // All URLs failed
                          console.error('All image URLs failed:', fallbackUrls);
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="text-red-400 text-xs p-2 text-center">Failed to load</div>';
                        }
                      }}
                    />
                  ) : (
                    <div className="text-gray-400">
                      {getFileIcon(item.file_type)}
                    </div>
                  )}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(item);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteMedia(item.id);
                      }}
                      className="h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-medium truncate">{item.original_filename}</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500">{formatFileSize(item.file_size)}</p>
                  {item.storage_provider && item.storage_provider !== 'local' && (
                    <Badge variant="outline" className="text-xs text-green-600">
                      CDN
                    </Badge>
                  )}
                  {item.folder && (
                    <Badge variant="secondary" className="text-xs">
                      <FolderOpen className="h-3 w-3 mr-1" />
                      {item.folder}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Files selected: {selectedFiles.length}</Label>
              <div className="mt-2 space-y-1">
                {selectedFiles.map((file, idx) => (
                  <div key={file.id || file.name || `${file.title || "file"}-${idx}`} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-gray-500">{formatFileSize(file.size)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Folder (optional)</Label>
              <Input
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
                placeholder="products, blog, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsUploadModalOpen(false);
                setSelectedFiles([]);
                setUploadFolder('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Media</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>File name</Label>
              <Input value={currentMedia?.original_filename || ''} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label>Alt Text</Label>
              <Input
                value={formData.alt_text}
                onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                placeholder="Describe the image for accessibility"
              />
            </div>
            <div>
              <Label>Caption</Label>
              <Input
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                placeholder="Add a caption"
              />
            </div>
            <div>
              <Label>Tags</Label>
              <TagInput
                tags={formData.tags}
                onChange={(tags) => setFormData({ ...formData, tags })}
                tagType="media"
                placeholder="Add tags..."
              />
            </div>
            <div>
              <Label>Folder</Label>
              <Input
                value={formData.folder}
                onChange={(e) => setFormData({ ...formData, folder: e.target.value })}
                placeholder="e.g., products, blog"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateMedia}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>File Details</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="bg-gray-100 rounded-lg flex items-center justify-center p-4">
              {currentMedia?.file_type === 'image' ? (
                <img 
                  src={getOptimizedImageUrl(currentMedia, 800)} 
                  alt={currentMedia.alt_text || currentMedia.original_filename}
                  className="max-h-96 object-contain"
                  onError={(e) => {
                    // Try fallback URLs for detail modal
                    const fallbackUrls = currentMedia ? getImageFallbackUrls(currentMedia) : [];
                    const currentSrc = e.target.src;
                    const currentIndex = fallbackUrls.indexOf(currentSrc);
                    const nextIndex = currentIndex + 1;
                    
                    if (nextIndex < fallbackUrls.length) {
                      e.target.src = fallbackUrls[nextIndex];
                    } else {
                      e.target.alt = 'Failed to load image';
                    }
                  }}
                />
              ) : (
                <div className="text-gray-400">
                  {getFileIcon(currentMedia?.file_type)}
                </div>
              )}
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">File name</Label>
                <p className="font-medium">{currentMedia?.original_filename}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">File type</Label>
                <p className="font-medium capitalize">{currentMedia?.file_type}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Size</Label>
                <p className="font-medium">{formatFileSize(currentMedia?.file_size || 0)}</p>
              </div>
              {currentMedia?.width && currentMedia?.height && (
                <div>
                  <Label className="text-xs text-gray-500">Dimensions</Label>
                  <p className="font-medium">{currentMedia.width} × {currentMedia.height}</p>
                </div>
              )}
              {currentMedia?.alt_text && (
                <div>
                  <Label className="text-xs text-gray-500">Alt text</Label>
                  <p className="font-medium">{currentMedia.alt_text}</p>
                </div>
              )}
              {currentMedia?.folder && (
                <div>
                  <Label className="text-xs text-gray-500">Folder</Label>
                  <Badge variant="secondary" className="mt-1">
                    <FolderOpen className="h-3 w-3 mr-1" />
                    {currentMedia.folder}
                  </Badge>
                </div>
              )}
              {currentMedia?.tags && currentMedia.tags.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">Tags</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {currentMedia.tags.map((tag, idx) => (
                      <Badge key={tag.id || tag.name || `${tag.title || "tag"}-${idx}`} variant="secondary">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {/* CDN Storage Info */}
              <div>
                <Label className="text-xs text-gray-500">Storage</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {currentMedia?.storage_provider === 'dual' && (
                    <Badge variant="outline" className="text-green-600">DO + Shopify CDN</Badge>
                  )}
                  {currentMedia?.storage_provider === 'do_spaces' && (
                    <Badge variant="outline" className="text-blue-600">DO Spaces</Badge>
                  )}
                  {currentMedia?.storage_provider === 'local' && (
                    <Badge variant="outline" className="text-gray-600">Local</Badge>
                  )}
                  {currentMedia?.cdn_sync_status === 'synced' && (
                    <Badge variant="outline" className="text-green-600">✓ Synced</Badge>
                  )}
                  {currentMedia?.cdn_sync_status === 'partial' && (
                    <Badge variant="outline" className="text-yellow-600">⚠ Partial</Badge>
                  )}
                </div>
              </div>
              <div className="pt-4 flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    openEditModal(currentMedia);
                  }}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    handleDeleteMedia(currentMedia.id);
                    setIsDetailModalOpen(false);
                  }}
                  className="flex-1 text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MediaGallery;
